import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../../services/api';
import { wsService, type WebSocketMessage } from '../../../services/websocket';
import type { Campaign, Lead } from '../../../types';
import { groupLeadsByFunilStage, type FunilIaStageSlug } from '../funilIaStage';
import { funilCampaignSwatchForList, type FunilCampaignSwatch } from '../funilCampaignPalette';

export interface UseFunilIaDataReturn {
  campaigns: Campaign[];
  leadsByCampaign: Record<string, Lead[]>;
  allFunnelLeads: Lead[];
  grouped: Record<FunilIaStageSlug, Lead[]>;
  loading: boolean;
  error: string | null;
  /** Persiste aiFunnelHandoffAt e atualiza estado local. */
  persistHandoff: (leadId: string) => Promise<void>;
  /** Lookup de swatch por campaignId, alinhado à ordem da lista de campanhas. */
  swatchForCampaign: (campaignId: string) => FunilCampaignSwatch;
}

function findCampaignIdForLead(leadsByCampaign: Record<string, Lead[]>, leadId: string): string | null {
  for (const cid of Object.keys(leadsByCampaign)) {
    if (leadsByCampaign[cid]?.some((l) => l.leadId === leadId)) return cid;
  }
  return null;
}

/** Merge superficial + `iaConversationContext` a fundir (evita perder `handoffBin` com patches WS parciais). */
function mergeLeadPatch(lead: Lead, patch: Partial<Lead>): Lead {
  const base = { ...lead, ...patch };
  const pCtx = patch.iaConversationContext;
  const lCtx = lead.iaConversationContext;
  if (pCtx && typeof pCtx === 'object' && lCtx && typeof lCtx === 'object') {
    base.iaConversationContext = { ...lCtx, ...pCtx };
  }
  return base;
}

/**
 * Hook compartilhado de dados do Funil IA.
 * Encapsula: loading de campanhas ai_funnel, loading de leads por campanha,
 * WebSocket subscriptions, visibility reload, fallback polling,
 * persistência de handoff e campaign palette.
 *
 * Usado tanto pelo FunilIaSlides quanto pelo FunilIaPhysics.
 */
export function useFunilIaData(): UseFunilIaDataReturn {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [leadsByCampaign, setLeadsByCampaign] = useState<Record<string, Lead[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const list = await apiService.listCampaigns({ status: 'ativa', attendanceMode: 'ai_funnel' });
      setCampaigns(list);
      const map: Record<string, Lead[]> = {};
      await Promise.all(
        list.map(async (c) => {
          const leads: Lead[] = await apiService.getLeads({ inboundCampaignId: c.campaignId });
          map[c.campaignId] = leads;
        })
      );
      setLeadsByCampaign(map);
    } catch (e: unknown) {
      if (!silent) {
        setError(e instanceof Error ? e.message : 'Erro ao carregar funil IA');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const refreshCampaignLeads = useCallback(async (campaignId: string) => {
    try {
      const leads: Lead[] = await apiService.getLeads({ inboundCampaignId: campaignId });
      setLeadsByCampaign((prev) => ({ ...prev, [campaignId]: leads }));
    } catch {
      /* noop */
    }
  }, []);

  // Initial load
  useEffect(() => {
    void load();
  }, [load]);

  // Visibility change reload
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  // Fallback polling (90s) when WS disconnected
  useEffect(() => {
    const tick = () => {
      if (!wsService.isConnected()) void load({ silent: true });
    };
    const id = window.setInterval(tick, 90_000);
    return () => window.clearInterval(id);
  }, [load]);

  // WebSocket subscriptions
  const funnelCampaignIds = useMemo(() => new Set(campaigns.map((c) => c.campaignId)), [campaigns]);

  useEffect(() => {
    const handleLeadUpdated = (msg: WebSocketMessage) => {
      if (msg.type !== 'lead_updated' || !msg.data) return;
      const patch = msg.data as Partial<Lead>;
      const cid = (patch.inboundCampaignId || '').trim();

      if (cid && funnelCampaignIds.has(cid)) {
        void refreshCampaignLeads(cid);
        return;
      }

      if (msg.leadId) {
        setLeadsByCampaign((prev) => {
          let changed = false;
          const next: Record<string, Lead[]> = { ...prev };
          for (const key of Object.keys(next)) {
            if (!funnelCampaignIds.has(key)) continue;
            const list = next[key];
            const idx = list.findIndex((l) => l.leadId === msg.leadId);
            if (idx >= 0) {
              next[key] = list.map((l, i) => (i === idx ? mergeLeadPatch(l, patch) : l));
              changed = true;
            }
          }
          if (!changed) {
            /** 1.ª mensagem / sessão: o lead pode ainda não estar na lista em memória — recarrega o funil. */
            void load({ silent: true });
          }
          return changed ? next : prev;
        });
      }
    };

    const handleCampaignItemUpdated = (msg: WebSocketMessage) => {
      if (msg.type !== 'campaign_item_updated' || !msg.data) return;
      const raw = msg.data as { movedItems?: Array<{ campaignId?: string }> };
      const moved = raw.movedItems;
      if (!Array.isArray(moved) || moved.length === 0) return;
      const toRefresh = new Set<string>();
      for (const u of moved) {
        const cid = (u?.campaignId || '').trim();
        if (cid && funnelCampaignIds.has(cid)) toRefresh.add(cid);
      }
      toRefresh.forEach((cid) => void refreshCampaignLeads(cid));
    };

    wsService.on('lead_updated', handleLeadUpdated);
    wsService.on('campaign_item_updated', handleCampaignItemUpdated);
    return () => {
      wsService.off('lead_updated', handleLeadUpdated);
      wsService.off('campaign_item_updated', handleCampaignItemUpdated);
    };
  }, [funnelCampaignIds, refreshCampaignLeads, load]);

  // Derived data
  const allFunnelLeads = useMemo(() => {
    const out: Lead[] = [];
    for (const c of campaigns) {
      for (const l of leadsByCampaign[c.campaignId] ?? []) {
        out.push({
          ...l,
          inboundCampaignId: l.inboundCampaignId ?? c.campaignId,
        });
      }
    }
    return out;
  }, [campaigns, leadsByCampaign]);

  const grouped = useMemo(() => groupLeadsByFunilStage(allFunnelLeads), [allFunnelLeads]);

  // Handoff persistence
  const persistHandoff = useCallback(async (leadId: string) => {
    const cid = findCampaignIdForLead(leadsByCampaign, leadId);
    if (!cid) return;
    try {
      const updated = await apiService.updateLead(leadId, {
        aiFunnelHandoffAt: new Date().toISOString(),
      });
      setLeadsByCampaign((prev) => {
        const list = prev[cid];
        if (!list) return prev;
        return {
          ...prev,
          [cid]: list.map((l) => (l.leadId === leadId ? { ...l, ...updated } : l)),
        };
      });
    } catch (e) {
      console.error('[FunilIaData] persist handoff', e);
    }
  }, [leadsByCampaign]);

  // Campaign palette
  const swatchForCampaign = useMemo(
    () => (campaignId: string) => funilCampaignSwatchForList(campaigns, campaignId),
    [campaigns]
  );

  return {
    campaigns,
    leadsByCampaign,
    allFunnelLeads,
    grouped,
    loading,
    error,
    persistHandoff,
    swatchForCampaign,
  };
}
